import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useAccordionEventHandlers } from 'lib/Hooks';
import styles from './styles.module.scss';

/**
 * @typedef AccordionAdditionalStyles
 * @property {string} accordion
 * @property {string} open
 * @property {string} accordion__header
 * @property {string} accordion__icon
 * @property {string} accordion__content
 */

/**
 * Accordion component.
 *
 * @param {object} props
 * @param {boolean} props.isCollapsible
 * @param {boolean} props.isOpen
 * @param {'down'|'up'} props.openDirection
 * @param {string} props.testId
 * @param {string} props.title
 * @param {Function} props.onToggle
 * @param {Function} props.onFocus
 * @param {boolean} props.showAffordanceWithCount
 * @param {boolean} props.suppressScroll
 * @param {string} props.testId
 * @param {string} props.title
 * @param {AccordionAdditionalStyles} props.additionalStyles
 * @param {React.Element} props.children
 * @returns {React.ReactElement|null}
 */
const Accordion = forwardRef(({
  additionalStyles = {},
  children,
  isCollapsible = true,
  suppressScroll,
  isOpen: controlledIsOpen,
  openDirection = 'down',
  testId = 'accordion',
  title,
  onToggle = () => {},
  onFocus,
  showAffordanceWithCount,
}, ref) => {
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const contentRef = useRef(null);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : localIsOpen;
  const hasAdditionalStyles = Object.keys(additionalStyles).length > 0;


  /**
   * Local toggle function.
   */
  const toggle = () => {
    if (isControlled) {
      onToggle();
    } else {
      setLocalIsOpen((prev) => !prev);
    }
  };

  const { button, content } = useAccordionEventHandlers(
    (isOpen && isCollapsible) || showAffordanceWithCount,
    contentRef,
    toggle,
  );

  useEffect(() => {
    /**
     * On mobile, when the content window is greater than 60% of the viewport, we will show the affordance, unless suppressScroll was set.
     */
    const checkScrollable = () => {
      if (suppressScroll) {
        return;
      }

      const shouldCheckScrollable = (isCollapsible && isOpen && contentRef.current)
        || showAffordanceWithCount;

      if (!shouldCheckScrollable) {
        setIsScrollable(false);
      } else if (showAffordanceWithCount) {
        setIsScrollable(true);
      } else {
        const sixtyVh = window.innerHeight * 0.6;
        setIsScrollable(contentRef.current.scrollHeight > sixtyVh);
      }
    };

    checkScrollable();

    window.addEventListener('resize', checkScrollable);

    return () => {
      window.removeEventListener('resize', checkScrollable);
    };
  }, [isCollapsible, isOpen, showAffordanceWithCount]);

  if (!title || !children) {
    return null;
  }

  const accordionToggleButton = (
    <button
      aria-controls="accordion-content"
      aria-expanded={isOpen}
      className={classNames(
        styles.accordion__header,
        { [additionalStyles.accordion__header]: hasAdditionalStyles },
      )}
      data-testid="accordion__button"
      disabled={!isCollapsible}
      onClick={button.onClick}
      onKeyDown={button.onKeyDown}
      onFocus={onFocus}
      ref={ref}
      type="button"
      role="combobox"
    >
      {title}
      {(isCollapsible) && (
        <span
          className={classNames(
            styles.accordion__icon,
            { [additionalStyles.accordion__icon]: hasAdditionalStyles },
            'icon',
            'icon-chevron-light-down',
            { [styles.down]: isOpen },
          )}
          data-testid="accordion__toggle-icon"
        />
      )}
    </button>
  );

  return (
    <div
      className={classNames(
        styles.accordion,
        { [additionalStyles.accordion]: hasAdditionalStyles },
        { [styles.open]: isOpen },
        { [additionalStyles.open]: hasAdditionalStyles && isOpen },
        { [additionalStyles.closed]: hasAdditionalStyles && !isOpen },
        { [styles.closed]: !isOpen },
      )}
      data-testid={testId}
      data-title={title}
    >
      {openDirection === 'down' ? accordionToggleButton : null}
      <div
        aria-hidden={!isOpen}
        className={classNames(
          styles.accordion__content,
          { [additionalStyles.accordion__content]: hasAdditionalStyles },
          { [styles.open]: isOpen && !additionalStyles.open },
          { [additionalStyles.open]: hasAdditionalStyles && isOpen },
          { [styles.closed]: !isOpen },
        )}
        data-testid="accordion__content"
        ref={contentRef}
        role="listbox"
        tabIndex={-1}
        onKeyDown={content.onKeyDown}
      >
        {children}
        {/* Affordance showing there are items below the fold */}
        {isScrollable && (
          <div
            className={classNames({ [styles.affordance]: !showAffordanceWithCount })}
            data-testid="accordion__affordance"
          />
        )}
      </div>
      {openDirection === 'up' ? accordionToggleButton : null}
    </div>
  );
});

export { Accordion };
